import api, { ApiResponse, PaginatedApiResponse } from './axiosInstance';

// User interface for populated fields
export interface User {
  _id: string;
  name: string;
  email?: string;
}

// Audit Type interface matching backend
export interface AuditType {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdBy: string | User;
  updatedBy: string | User;
  createdAt: string;
  updatedAt: string;
}

// Create Audit Type data interface
export interface CreateAuditTypeData {
  name: string;
  description?: string;
}

// Update Audit Type data interface (PATCH)
export interface UpdateAuditTypeData extends Partial<CreateAuditTypeData> {
  isActive?: boolean;
}

// Query parameters interface
export interface AuditTypeQueryParams {
  id?: string; // For getting single record
  page?: number;
  limit?: number;
  search?: string;
  name?: string;
  isActive?: boolean;
  sortBy?: string;
}

class AuditTypeService {
  private baseUrl = '/audit-types';

  private normalizeAuditType = (raw: any): AuditType => {
    // Handle populated user objects - keep the full object if populated, otherwise use ID string
    let createdBy: string | User = '';
    if (raw?.createdBy) {
      if (typeof raw.createdBy === 'object' && raw.createdBy !== null) {
        if (raw.createdBy.name) {
          createdBy = {
            _id: raw.createdBy._id || raw.createdBy.id || '',
            name: raw.createdBy.name,
            email: raw.createdBy.email
          };
        } else {
          createdBy = raw.createdBy._id || raw.createdBy.id || raw.createdBy.toString() || '';
        }
      } else {
        createdBy = raw.createdBy;
      }
    }

    let updatedBy: string | User = '';
    if (raw?.updatedBy) {
      if (typeof raw.updatedBy === 'object' && raw.updatedBy !== null) {
        if (raw.updatedBy.name) {
          updatedBy = {
            _id: raw.updatedBy._id || raw.updatedBy.id || '',
            name: raw.updatedBy.name,
            email: raw.updatedBy.email
          };
        } else {
          updatedBy = raw.updatedBy._id || raw.updatedBy.id || raw.updatedBy.toString() || '';
        }
      } else {
        updatedBy = raw.updatedBy;
      }
    }

    return {
      _id: raw?._id ?? raw?.id ?? '',
      name: raw?.name ?? '',
      description: raw?.description,
      isActive: raw?.isActive ?? true,
      createdBy,
      updatedBy,
      createdAt: raw?.createdAt ?? '',
      updatedAt: raw?.updatedAt ?? '',
    } as AuditType;
  };

  /**
   * Get all Audit Types with pagination and filtering, or get by ID
   */
  async getAuditTypes(params?: AuditTypeQueryParams): Promise<PaginatedApiResponse<AuditType> | ApiResponse<AuditType>> {
    try {
      const response = await api.get<PaginatedApiResponse<any> | ApiResponse<any>>(this.baseUrl, { params });

      // If id is provided, return single record response
      if (params?.id) {
        const normalized: ApiResponse<AuditType> = {
          ...response.data,
          data: this.normalizeAuditType(response.data?.data),
        };
        return normalized;
      }

      // Check if response has meta (paginated) or just data (non-paginated)
      if ('meta' in response.data) {
        const normalized: PaginatedApiResponse<AuditType> = {
          ...response.data,
          data: (response.data?.data ?? []).map((a: any) => this.normalizeAuditType(a)),
        };
        return normalized;
      } else {
        return {
          success: response.data.success,
          message: response.data.message,
          data: (response.data?.data ?? []).map((a: any) => this.normalizeAuditType(a)),
        };
      }
    } catch (error: any) {
      console.error('Error fetching Audit Types:', error);
      throw new Error(error.message || 'Failed to fetch Audit Types');
    }
  }

  /**
   * Get a single Audit Type by ID
   */
  async getAuditTypeById(id: string): Promise<ApiResponse<AuditType>> {
    try {
      const response = await api.get<ApiResponse<any>>(`${this.baseUrl}?id=${id}`);
      const normalized: ApiResponse<AuditType> = {
        ...response.data,
        data: this.normalizeAuditType(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error fetching Audit Type:', error);
      throw new Error(error.message || 'Failed to fetch Audit Type');
    }
  }

  /**
   * Get all currently-active Audit Types, paged through with a large limit.
   * Convenience helper for dropdowns / checkbox lists that need the full flat list.
   */
  async getAllActiveAuditTypes(): Promise<AuditType[]> {
    try {
      const response = await api.get<PaginatedApiResponse<any>>(this.baseUrl, {
        params: { isActive: true, limit: 1000, page: 1 },
      });
      return (response.data?.data ?? []).map((a: any) => this.normalizeAuditType(a));
    } catch (error: any) {
      console.error('Error fetching active Audit Types:', error);
      throw new Error(error.message || 'Failed to fetch active Audit Types');
    }
  }

  /**
   * Create a new Audit Type
   */
  async createAuditType(auditTypeData: CreateAuditTypeData): Promise<ApiResponse<AuditType>> {
    try {
      const response = await api.post<ApiResponse<any>>(this.baseUrl, auditTypeData);
      const normalized: ApiResponse<AuditType> = {
        ...response.data,
        data: this.normalizeAuditType(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error creating Audit Type:', error);
      throw new Error(error.message || 'Failed to create Audit Type');
    }
  }

  /**
   * Update an Audit Type (PATCH)
   */
  async updateAuditType(id: string, auditTypeData: UpdateAuditTypeData): Promise<ApiResponse<AuditType>> {
    try {
      const response = await api.patch<ApiResponse<any>>(`${this.baseUrl}/${id}`, auditTypeData);
      const normalized: ApiResponse<AuditType> = {
        ...response.data,
        data: this.normalizeAuditType(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error updating Audit Type:', error);
      throw new Error(error.message || 'Failed to update Audit Type');
    }
  }

  /**
   * Activate an Audit Type
   */
  async activateAuditType(id: string): Promise<ApiResponse<AuditType>> {
    try {
      const response = await api.post<ApiResponse<any>>(`${this.baseUrl}/${id}/activate`);
      const normalized: ApiResponse<AuditType> = {
        ...response.data,
        data: this.normalizeAuditType(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error activating Audit Type:', error);
      throw new Error(error.message || 'Failed to activate Audit Type');
    }
  }

  /**
   * Inactivate an Audit Type
   */
  async inactivateAuditType(id: string): Promise<ApiResponse<AuditType>> {
    try {
      const response = await api.post<ApiResponse<any>>(`${this.baseUrl}/${id}/inactivate`);
      const normalized: ApiResponse<AuditType> = {
        ...response.data,
        data: this.normalizeAuditType(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error inactivating Audit Type:', error);
      throw new Error(error.message || 'Failed to inactivate Audit Type');
    }
  }
}

export const auditTypeService = new AuditTypeService();
export default auditTypeService;
